<!-- eslint-disable no-use-before-define -->
<script setup>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faMinus,
  faPlus,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { DateTime } from 'luxon';
import {
  computed,
  defineProps,
  reactive,
  ref,
} from 'vue';

import { afterEnter, enter, leave } from '@/helpers/transitions/accordion';
import { getAssetUrl } from '@/helpers/url';
import { i18n } from '@/i18n';

library.add({
  faMinus,
  faPlus,
  faTimes,
});

const props = defineProps({
  concerns: {
    type: Array,
    required: true,
  },
});

const concernColumns = ref([
  props.concerns.filter((concern, i) => i % 2 === 0),
  props.concerns.filter((concern, i) => i % 2 === 1),
].map((concerns) => concerns.map((concern) => reactive({
  name: concern.name,
  slug: concern.slug,
  posts: concern.posts,
  open: false,
}))));

const concernsCount = props.concerns.length;

const concernsDefaultMobileLimit = 6;

const concernsLimit = ref(concernsDefaultMobileLimit);

const concernsButtonIcon = computed(() => (isShowingAllConcerns.value
  ? ['fal', 'minus']
  : ['fal', 'plus']
));

const concernsButtonText = computed(() => (isShowingAllConcerns.value
  ? i18n.t('homepage.less-concerns')
  : i18n.t('homepage.more-concerns')
));

const isShowingAllConcerns = computed(() => concernsLimit.value === concernsCount);

function getPostCover(post) {
  return post.cover_url || getAssetUrl('/images/vetster-image-placeholder.svg');
}

function getPostInfo(post) {
  const date = DateTime
    .fromISO(post.posted_on, { zone: this.timezone })
    .toLocaleString(DateTime.DATE_FULL);

  return `${date} • ${i18n.t('blogpost.min-read', { mins: post.read_time })}`;
}

function hasPosts(concern) {
  return concern.posts?.length > 0;
}

function toggleConcern(concern) {
  if (hasPosts(concern)) {
    concern.open = !concern.open;
  }
}

function toggleConcernsLimit() {
  concernsLimit.value = concernsLimit.value === concernsDefaultMobileLimit
    ? concernsCount
    : concernsDefaultMobileLimit;
}
</script>

<template>
  <div class="relative flex flex-col justify-center">
    <div class="flex flex-col md:flex-row gap-2 m-auto w-full md:max-w-3xl">
      <ul
        v-for="(concernsInColumn, i) in concernColumns"
        :key="`concern-${i}`"
        class="flex flex-col gap-y-2 text-ocean-100 w-full"
      >
        <li
          v-for="(concern, j) in concernsInColumn"
          :key="`concern-${i}-${j}`"
          class="bg-ocean-900 rounded-lg flex-col group relative w-full"
          :class="(i === 0 ? j : concernColumns[0].length + j) >= concernsLimit ? 'hidden md:flex' : 'flex'"
        >
          <div
            class="absolute top-0 left-0 w-full h-full border border-ocean-100 pointer-events-none rounded-lg"
            :class="{
              hidden: !concern.open,
              'group-hover:block': hasPosts(concern),
            }"
          />
          <button
            class="w-full text-left p-5 flex justify-between items-center"
            :class="{ 'pointer-events-none': !hasPosts(concern) }"
            :aria-label="concern.name"
            type="button"
            @click="toggleConcern(concern)"
          >
            <span :class="{ 'font-bold': concern.open }">
              {{ concern.name }}
            </span>
            <fa
              v-if="hasPosts(concern)"
              :icon="['fal', 'plus']"
              class="transition"
              :class="concern.open ? 'rotate-45' : 'rotate-0'"
            />
          </button>
          <transition
            name="accordion"
            @enter="enter"
            @after-enter="afterEnter"
            @leave="leave"
          >
            <div
              v-show="concern.open"
              class="transition-all duration-200 ease-in-out overflow-hidden w-full px-5"
            >
              <a
                v-for="post in concern.posts"
                :key="post.title"
                :href="post.url"
                class="flex items-center justify-between border-t border-white/10 py-4 w-full group/post"
                rel="noreferrer noopener"
              >
                <div class="flex items-start w-full">
                  <img
                    :src="getPostCover(post)"
                    :alt="post.title"
                    class="rounded-lg w-11 h-11"
                  />
                  <div class="flex flex-col justify-center text-sm px-3">
                    <span class="text-ocean-50 leading-5 group-hover/post:underline">
                      {{ post.title }}
                    </span>
                    <div class="text-ocean-200 mt-0.5">
                      {{ getPostInfo(post) }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center justify-center rounded-full border border-white w-10 h-9 group-hover/post:bg-ocean-200/10">
                  <img
                    class="w-4"
                    :src="getAssetUrl('/images/arrows/straight-short-white-arrow.svg')"
                    aria-hidden="true"
                    alt=""
                  />
                </div>
              </a>
            </div>
          </transition>
        </li>
      </ul>
    </div>
    <button
      type="button"
      class="bottom-0 left-0 w-full flex md:hidden items-center space-x-2 text-ocean-100 to-50% justify-center bg-gradient-to-b from-transparent to-ocean-950"
      :class="isShowingAllConcerns ? 'pt-7' : 'absolute pt-20'"
      :aria-label="concernsButtonText"
      @click="toggleConcernsLimit"
    >
      <span class="underline underline-offset-4">
        {{ concernsButtonText }}
      </span>
      <fa
        class="text-lg"
        :icon="concernsButtonIcon"
      />
    </button>
  </div>
</template>
